<template>
  <div class="solar-system-game">
    <LevelComplete v-if="isGameComplete" />
    <div v-else>
      <h1
        v-if="targetPlanet"
        class="target-planet"
        @click="speakOutLoud(targetPlanet.name)"
      >
        {{ targetPlanet.name }}
      </h1>

      <!-- Planet image -->
      <div
        v-if="targetPlanet"
        class="planet-image"
      >
        <img
          :src="targetPlanet.image"
          :alt="targetPlanet.name"
        >
      </div>

      <p
        v-if="message"
        class="message"
      >
        {{ message }}
      </p>

      <div class="planet-buttons">
        <button
          v-for="(planet, index) in shuffledPlanets"
          :key="index"
          class="planet-button"
          @click="handlePlanetClick(planet)"
        >
          <img
            :src="planet.image"
            :alt="planet.name"
          >
          <span>{{ planet.name }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import LevelComplete from "../LevelComplete.vue";
import { speechMixin } from "@/mixins/speechMixin.js";

import EarthImage from "@/assets/planets/Earth.png";

export default {
  components: {
    LevelComplete,
  },
  mixins: [speechMixin],
  data() {
    return {
      planets: [
        {
          name: "Mercury",
          image:
            "https://png.pngtree.com/png-clipart/20230801/original/pngtree-mercury-planet-isolated-on-white-background-picture-image_7805941.png",
        },
        {
          name: "Venus",
          image:
            "https://images.vexels.com/media/users/3/205478/isolated/lists/4a42dba3e55a9bcc9764720d87f2ca93-planet-venus-illustration.png",
        },
        {
          name: "Earth",
          image: EarthImage,
        },
        {
          name: "Mars",
          image:
            "https://www.pngplay.com/wp-content/uploads/4/Mars-PNG-Images-HD.png",
        },
        {
          name: "Jupiter",
          image: "https://cdn-icons-png.flaticon.com/512/124/124558.png",
        },
        {
          name: "Saturn",
          image:
            "https://img.pikbest.com/origin/09/22/96/52MpIkbEsTJDS.png!sw800",
        },
        {
          name: "Uranus",
          image:
            "https://www.pngplay.com/wp-content/uploads/13/Uranus-PNG-HD-Quality.png",
        },
        {
          name: "Neptune",
          image:
            "https://static.vecteezy.com/system/resources/previews/020/004/117/non_2x/neptune-planet-illustration-png.png",
        },
      ],
      shuffledPlanets: [],
      targetPlanetIndex: 0,
      isGameComplete: false,
      message: "",
      selectedVoice: null,
    };
  },
  computed: {
    targetPlanet() {
      if (this.targetPlanetIndex < this.planets.length) {
        return this.planets[this.targetPlanetIndex];
      } else {
        return null;
      }
    },
  },
  mounted() {
    this.setupVoices();
    this.setupGame();
  },
  methods: {
    setupGame() {
      this.targetPlanetIndex = 0;
      this.isGameComplete = false;
      this.shuffledPlanets = this.shuffleArray(this.planets);
      setTimeout(() => {
        this.promptNextPlanet();
      }, 1000);
    },
    promptNextPlanet() {
      if (this.isGameComplete) return;

      if (this.targetPlanetIndex >= this.planets.length) {
        this.isGameComplete = true;
        this.playSound(
          "https://codeskulptor-demos.commondatastorage.googleapis.com/descent/gotitem.mp3"
        );
        this.speakOutLoud("Great Job!");
        return;
      }

      // Reshuffle planets for buttonsx
      this.shuffledPlanets = this.shuffleArray(this.planets);

      // Ensure targetPlanet exists before accessing its properties
      if (this.targetPlanet) {
        const planetName = this.targetPlanet.name;
        this.speakOutLoud(`Find the planet ${planetName}!`);
      }
    },
    handlePlanetClick(selectedPlanet) {
      if (this.isGameComplete) return;

      if (selectedPlanet.name === this.targetPlanet.name) {
        this.message = "✅ Correct!";
        this.playSound(
          "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
        );
        this.speakOutLoud(selectedPlanet.name);
        this.targetPlanetIndex++;

        if (this.targetPlanetIndex >= this.planets.length) {
          // Game is complete
          this.isGameComplete = true; // Set immediately to prevent issues
          setTimeout(() => {
            this.$router.push("/planets");
          }, 4000);
        } else {
          setTimeout(() => {
            this.message = "";
            this.promptNextPlanet();
          }, 2000);
        }
      } else {
        this.message = "❌ Try Again!";
        setTimeout(() => {
          this.message = "";
        }, 2000);
      }
    },
    playSound(url) {
      const audio = new Audio(url);
      audio.play();
    },
    shuffleArray(array) {
      let arr = [...array];
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    },
  },
};
</script>

<style scoped>
.solar-system-game {
  text-align: center;
  margin: 20px;
}

.target-planet {
  font-size: 3rem;
  color: #ff9800;
  margin-bottom: 20px;
  cursor: pointer;
}

.planet-image {
  width: 200px;
  height: 200px;
  margin: auto;
  margin-bottom: 20px;
}

.planet-image img {
  max-width: 100%;
  max-height: 100%;
}

.message {
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 20px;
}

.planet-buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
}

.planet-button {
  background-color: #2196f3;
  color: white;
  border: none;
  border-radius: 15px;
  padding: 10px;
  width: 100px;
  cursor: pointer;
  transition: transform 0.3s;
}

.planet-button:hover {
  transform: scale(1.1);
}

.planet-button img {
  width: 50px;
  height: 50px;
}

.planet-button span {
  display: block;
  margin-top: 5px;
  font-size: 1rem;
}
</style>
